import { Injectable, signal, computed } from '@angular/core';
import { Subject } from 'rxjs';
import {
  signIn,
  signUp,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  type SignInOutput,
  type SignUpOutput,
  type ResetPasswordOutput,
} from 'aws-amplify/auth';
import { environment } from '../../environments/environment';
import { UserProfileService } from './user-profile.service';

export interface AuthUser {
  userId: string;
  email: string | undefined;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _isAuthenticated = signal<boolean>(false);
  private readonly _user = signal<AuthUser | null>(null);
  private readonly _loading = signal<boolean>(true);
  private readonly _error = signal<string | null>(null);
  private readonly _returnUrl = signal<string | null>(null);

  /** Subject to trigger login dialog from anywhere */
  private readonly _showLogin = new Subject<void>();
  readonly showLogin$ = this._showLogin.asObservable();

  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly user = this._user.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  readonly userEmail = computed(() => this._user()?.email ?? null);

  readonly isAdmin = computed(() => {
    const email = this._user()?.email;
    if (!email) return false;
    const admins = environment.adminEmails ?? [];
    return admins.includes(email);
  });

  constructor(private readonly userProfile: UserProfileService) {
    this.checkAuth();
  }

  private async checkAuth(): Promise<void> {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      const payload = idToken?.payload;

      if (user && payload) {
        this._user.set({
          userId: payload.sub as string,
          email: (payload['email'] as string) ?? (payload['cognito:username'] as string),
        });
        this._isAuthenticated.set(true);
        this.userProfile.fetchProfile();
      } else {
        this._user.set(null);
        this._isAuthenticated.set(false);
        this.userProfile.clearProfile();
      }
    } catch {
      this._user.set(null);
      this._isAuthenticated.set(false);
    } finally {
      this._loading.set(false);
    }
  }

  async getToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }

  async signIn(email: string, password: string): Promise<SignInOutput> {
    this._error.set(null);
    const result = await signIn({
      username: email,
      password,
    });
    await this.checkAuth();
    return result;
  }

  async signUp(email: string, password: string): Promise<SignUpOutput> {
    this._error.set(null);
    return signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
        },
      },
    });
  }

  async confirmSignUp(email: string, code: string): Promise<void> {
    this._error.set(null);
    await confirmSignUp({
      username: email,
      confirmationCode: code,
    });
    await this.checkAuth();
  }

  async resendSignUpCode(email: string): Promise<void> {
    this._error.set(null);
    const { resendSignUpCode } = await import('aws-amplify/auth');
    await resendSignUpCode({ username: email });
  }

  async forgotPassword(email: string): Promise<ResetPasswordOutput> {
    this._error.set(null);
    return resetPassword({ username: email });
  }

  async confirmForgotPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<void> {
    this._error.set(null);
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  }

  async signOut(): Promise<void> {
    this._error.set(null);
    await signOut({ global: true });
    this._user.set(null);
    this._isAuthenticated.set(false);
    this.userProfile.clearProfile();
  }

  setError(message: string | null): void {
    this._error.set(message);
  }

  clearError(): void {
    this._error.set(null);
  }

  refreshAuth(): void {
    this.checkAuth();
  }

  /** Trigger the login dialog to appear */
  requestLogin(): void {
    this._showLogin.next();
  }

  setReturnUrl(url: string): void {
    this._returnUrl.set(url);
  }

  getAndClearReturnUrl(): string | null {
    const url = this._returnUrl();
    this._returnUrl.set(null);
    return url;
  }
}
